import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, isDevMode, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import { TenantService } from '@hta/auth';

/**
 * Visor de diagnóstico del corporativo activo. Solo se renderiza en desarrollo.
 *
 * Muestra:
 *  - email del usuario autenticado
 *  - cantidad de tenants en cache
 *  - tenant actual seleccionado
 *  - resultado del último intento de GET /api/auth/tenants (status, body)
 *
 * Acciones:
 *  - Refrescar tenants (forzar llamada)
 *  - Limpiar caché y recargar
 *  - Copiar JSON
 */
@Component({
    selector: 'app-tenant-debug-widget',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
    templateUrl: './tenant-debug-widget.component.html',
    styleUrl: './tenant-debug-widget.component.scss',
})
export class TenantDebugWidgetComponent {
    private readonly tenantService = inject(TenantService);
    private readonly router = inject(Router);
    private readonly cdr = inject(ChangeDetectorRef);

    readonly visible = signal(false);
    readonly tenants = this.tenantService.tenants;
    readonly currentTenant = this.tenantService.currentTenant;
    readonly lastCall = this.tenantService.lastCall;

    readonly stateLabel = computed<string>(() => {
        const lc = this.lastCall();
        if (!lc) return 'Sin llamadas registradas';
        if (lc.ok === null) return 'En curso…';
        if (lc.ok === true) return `OK ${lc.status ?? '?'}`;
        return `Error ${lc.status ?? '?'} ${lc.statusText ?? ''}`.trim();
    });

    readonly hasError = computed<boolean>(() => {
        const lc = this.lastCall();
        return !!lc && lc.ok === false;
    });

    toggle(): void {
        this.visible.update(v => !v);
        this.cdr.markForCheck();
    }

    async refresh(): Promise<void> {
        try {
            await this.tenantService.getTenants(true);
        } catch {
            /* el servicio ya registra el error en `lastCall` */
        }
    }

    hardReload(): void {
        window.location.assign('/');
    }

    goSelectTenant(): void {
        this.router.navigateByUrl('/tenant-selection');
    }

    copyJson(): void {
        const data = {
            tenants: this.tenants(),
            currentTenant: this.currentTenant(),
            lastCall: this.lastCall(),
        };
        const text = JSON.stringify(data, null, 2);
        if (navigator?.clipboard) {
            navigator.clipboard.writeText(text).catch(() => {/* ignore */});
        }
        if (typeof window !== 'undefined') {
            const w = window as Window & { __TENANT_DEBUG__?: unknown };
            w.__TENANT_DEBUG__ = data;
        }
    }
}
